import * as React from "react"
import { Link } from "gatsby"
import { StaticImage } from "gatsby-plugin-image"
import styled from "styled-components"
import { useStaticQuery, graphql } from "gatsby"

import Scheiner from "./menus/scheiner"
import Office from "./menus/office"
import Surgery from "./menus/surgery"
import NonSurgical from "./menus/nonSurgical"
import UnderEye from "./menus/underEye"
import Button from "../buttons/button"
import Logo from "../logo"

const StyledButton = styled(Button)`
  background: #222a3f;
  border-radius: 0;
  border: 1px solid #222a3f;
  text-decoration: none;
  transition: all 300ms;

  &:hover,
  &:focus {
    background: #fff;
    color: #222a3f;
  }
`

const StyledSection = styled.section({
  backgroundColor: `#F6F5F3`,
  padding: `2rem 1rem`,
  fontSize: `16px`,
})

const StyledWidth = styled.div({
  maxWidth: `1170px`,
  margin: `0 auto`,
})

const FlexBox = styled.div({
  display: `flex`,
  flexWrap: `wrap`,
})

const FlexBetween = styled(FlexBox)`
  justify-content: space-between;
  margin: 0 4rem 2rem;

  @media (max-width: 960px) {
    margin: 0 0 2rem;
  }

  @media (max-width: 672px) {
    justify-content: center;
    text-align: center;
  }
`

const FlexBetweenSmall = styled(FlexBetween)`
  max-width: 275px;
  width: 100%;
  text-align: right;
  align-items: center;
  margin: 0;

  @media (max-width: 672px) {
    max-width: 100%;
    min-with: 100%;
    display: flex;
    justify-content: center;
    margin-top: 1rem;
  }
`

const FlexCenter = styled(FlexBox)({
  justifyContent: `center`,
})

const StyledH2 = styled.h2({
  fontSize: `32px`,
  marginBottom: `0`,
  marginTop: `0`,
})

const StyledH2B = styled.h2({
  margin: `0 0 1rem`,
  padding: 0,
  lineHeight: 1,
  fontSize: `24px`,
})

const StyledHr = styled.hr({
  width: `100%`,
  color: `#222A3F`,
  marginBottom: `3rem`,
})

const StyledLink = styled(Link)({
  color: `#222A3F`,
  textDecoration: `none`,
  fontSize: `24px`,
  fontWeight: 700,
  textTransform: `uppercase`,
})

const LogoWrap = styled.div`
  @media (max-width: 1192px) {
    flex-wrap: wrap;
  }
  @media (min-width: 600px) and (max-width: 1192px) {
    justify-content: space-between;
    width: 100%;
    margin: 0 0.75rem 1.5rem;
  }

  @media (max-width: 600px) {
    margin-right: auto;
    display: flex;
    flex-direction: column;
  }

  @media (max-width: 500px) {
    align-items: center;
    margin-left: auto;
  }
`

const StyledPrivacyLink = styled(Link)({
  color: `#000`,
  textDecoration: `none`,
  fontWeight: 700,
  marginRight: `20px`,
})

const InlineLinks = styled.p( {
  marginBottom: `8px`
})

const StyledNav = styled.nav`
  ul {
    list-style-type: none;
    margin-left: 0;
  }

  > ul {
    columns: 3;
    column-gap: 3rem;

    @media (max-width: 600px) {
      columns: 2;
      column-gap: 1rem;
    }

    @media (max-width: 500px) {
      columns: 1;
    }
  }

  > ul > li {
    margin-bottom: 3rem;
    break-inside: avoid-column;
    margin-left: 0.75rem;
    max-width: calc(172.8px - 0.75rem);

    @media (max-width: 600px) {
      max-width: 50%;
      margin-left: 0;
    }

    @media (max-width: 500px) {
      max-width: 100%;
      min-width: 100%;
      text-align: center;

      &,
      &:first-of-type {
        margin-bottom: 2rem;
      }
    }
  }
`

const FollowWrap = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;

  a {
    transition: transform 300ms;

    &:hover,
    &:focus {
      transform: scale(1.1);
    }
  }
`

const StyledAddress = styled.address`
  table {
    max-width: 316px;
  }

  @media (max-width: 1192px) {
    order: 5;
    width: 100%;
    min-width: 100%;

    table {
      max-width: 100%;
      width: 100%;
    }
  }
`

const Menu = () => {
  const data = useStaticQuery(graphql`
  query GetStarted {
    # selecting the current post by id
    fields: wpPage(isFrontPage: { eq: true }) {
      footerSettingsFields {
        bottomCta {
          heading
          button {
            label
            url
          }
        }
      }
    }
  }
  `)

const Hours = styled.table`

`

  const bottomCta = data.fields.footerSettingsFields.bottomCta;

  return (
    <StyledSection>
      <StyledWidth>
        <FlexBetween>
          <div>
            <StyledH2>{bottomCta.heading}</StyledH2>
          </div>
          <FlexBetweenSmall>
            <StyledButton to={bottomCta.button.url}>
              {bottomCta.button.label}
            </StyledButton>
          </FlexBetweenSmall>
        </FlexBetween>
        <StyledHr />
        <a href="https://app.patientfi.com/v2/adamscheiner-md/apply" target="_blank" rel="noreferrer noopener">
          <StaticImage width={1170} src="../../images/ads/patient-fi.jpg" alt="PatientFi. Pay over time on a friendly monthly plan. Apply in 1 minute with no impact on your credit score. Get started." />
        </a>
        <StyledHr />
        <FlexCenter>
          <LogoWrap>
            <StyledH2B>
              <StyledLink to="/">
                <Logo />
              </StyledLink>
            </StyledH2B>
            <FollowWrap>
              <a href="https://www.facebook.com/adamscheinermd">
                <StaticImage src="../../images/icons/facebook.svg" alt="Like us on Facebook." />
              </a>

              <a href="https://pin.it/3FpGPPe">
                <StaticImage src="../../images/icons/pinterest.svg" alt="Follow on Pinterest." />
              </a>

              <a href="https://www.linkedin.com/company/adam-scheiner-m-d/">
                <StaticImage src="../../images/icons/linkedin.svg" alt="Follow on Linked In." />
              </a>

              <a href="https://www.instagram.com/adam_scheiner_md/">
                <StaticImage src="../../images/icons/instagram.svg" alt="Follow on Instagram." />
              </a>
            </FollowWrap>
            <StyledAddress>
              <table border="0">
                <tbody>
                  <tr>
                    <th scope="row">
                      <strong>Phone:</strong>
                    </th>
                    <td>
                      <a href="tel:813-367-1915">813.367.1915</a>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">
                      <strong>Aesthetic Center:</strong>
                    </th>
                    <td>4303 N. Gomez Ave, Tampa, FL</td>
                  </tr>
                </tbody>
              </table>
            </StyledAddress>
            <Hours>
              <caption>Open Hours</caption>
              <tbody>
                <tr>
                  <th scope="row">Monday</th>
                  <td>8am - 5pm</td>
                </tr>
                <tr>
                  <th scope="row">Tuesday</th>
                  <td>8am - 5pm</td>
                </tr>
                <tr>
                  <th scope="row">Wednesday</th>
                  <td>8am - 5pm</td>
                </tr>
                <tr>
                  <th scope="row">Thursday</th>
                  <td>8am - 5pm</td>
                </tr>
                <tr>
                  <th scope="row">Friday</th>
                  <td>8am - 2pm</td>
                </tr>
              </tbody>
            </Hours>
            <InlineLinks>
              <StyledPrivacyLink to="/privacy-policy">
                Privacy Policy
              </StyledPrivacyLink>
              <StyledPrivacyLink to="/terms-of-service">
                Terms of Service
              </StyledPrivacyLink>
            </InlineLinks>
            <p>
              &copy; 2010 - {new Date().getFullYear()}
            </p>
          </LogoWrap>
          <StyledNav aria-label="Footer" className="footer-menu">
            <ul>
              <Surgery />
              <NonSurgical />
              <Scheiner />
              <UnderEye />
              <Office />
            </ul>
          </StyledNav>
        </FlexCenter>
        <StyledHr />
        <small>
          Please Note: Procedure results vary greatly from patient to patient
          and are not guaranteed. The photos on this website are not intended to
          represent the results that every patient can expect. The information
          on this site is intended for general purposes only and is not intended
          to nor implied to be a substitute for professional medical advice
          relative to specific medical conditions or questions.
        </small>
      </StyledWidth>
    </StyledSection>
  )
}

export default Menu
